import { useCallback, useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

let fontCSS = null;
async function embedFonts() {
  if (fontCSS !== null) return fontCSS;
  try {
    const href = document.getElementById("bt-fonts")?.href;
    const css = await (await fetch(href)).text();
    const blocks = css.split("/*").filter((b) => b.trim().startsWith("latin */")).map((b) => b.slice(b.indexOf("*/") + 2));
    const toData = async (u) => {
      const blob = await (await fetch(u)).blob();
      return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
    };
    const out = await Promise.all(blocks.map(async (b) => {
      const m = b.match(/url\((https:[^)]+)\)/);
      return m ? b.replace(m[1], await toData(m[1])) : b;
    }));
    fontCSS = out.join("\n");
  } catch (e) {
    fontCSS = "";
  }
  return fontCSS;
}

export function useCertificate(slug, title) {
  const { settings } = useSite();
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);
  const [busy, setBusy] = useState(false);
  const ro = useRef(null);
  const boxRef = useCallback((node) => {
    ro.current?.disconnect();
    if (!node) return;
    ro.current = new ResizeObserver(() => setScale(node.offsetWidth / 1200 || 0.5));
    ro.current.observe(node);
  }, []);
  useEffect(() => () => ro.current?.disconnect(), []);
  const fileName = `${slug}-certificate.png`;
  const makeImage = async () => {
    await document.fonts.ready;
    return toPng(ref.current, { pixelRatio: 2, cacheBust: true, fontEmbedCSS: await embedFonts(), width: 1200, height: 848,
      style: { transform: "none", margin: "0" }, backgroundColor: getComputedStyle(document.documentElement).getPropertyValue("--bt-background").trim() || "#FCF9F0" });
  };
  const save = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  };
  const download = async () => {
    setBusy(true);
    try {
      save(await makeImage());
      toast.success("Certificate image saved");
    } catch (e) {
      toast.error("Couldn't create the image. Please try again.");
    }
    setBusy(false);
  };
  const share = async () => {
    setBusy(true);
    try {
      const url = await makeImage();
      const file = new File([await (await fetch(url)).blob()], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: `We completed ${title} with ${settings.brand.site_name}!` });
      } else {
        save(url);
        toast("Image downloaded. Upload it to Instagram or Facebook to share.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available here. Try Download instead.");
    }
    setBusy(false);
  };
  return { ref, boxRef, scale, busy, download, share };
}

export function CertificateArt({ c, name, date, kit, className = "" }) {
  const { settings } = useSite();
  return (
    <div ref={kit.boxRef} className={`relative w-full min-w-0 overflow-hidden rounded-[20px] shadow-xl print:max-w-none print:rounded-none print:shadow-none ${className}`} style={{ height: 848 * kit.scale }}>
      <div ref={kit.ref} className="absolute left-0 top-0 bg-bt-bg p-5" style={{ width: 1200, height: 848, transform: `scale(${kit.scale})`, transformOrigin: "top left" }} data-testid="certificate">
        <div className="relative flex h-full flex-col items-center justify-center rounded-[22px] border-[3px] border-bt-primary px-24 text-center">
          <div className="absolute inset-3 rounded-[16px] border border-bt-sagedeep opacity-50" />
          <svg viewBox="0 0 200 100" className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[90%] -translate-x-1/2 -translate-y-1/2 text-bt-sage" fill="none">
            <path d="M100 50c-18-26-38-38-58-38C20 12 6 30 6 50s14 38 36 38c20 0 40-12 58-38zm0 0c18 26 38 38 58 38 22 0 36-18 36-38s-14-38-36-38c-20 0-40 12-58 38z" stroke="currentColor" strokeWidth="5" />
          </svg>
          <div className="relative">
            <img src={mediaUrl(settings.brand.logo_url)} alt="" className="mx-auto mb-6 h-16" />
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-bt-sagedeep">{settings.brand.site_name}</p>
            <h1 className="mt-5 text-6xl">{c.certificate_title || "Certificate of Growth"}</h1>
            <p className="mt-8 text-xl">This certificate is proudly presented to</p>
            <p className="mt-5 border-b border-bt-sagedeep px-10 pb-3 font-heading text-6xl italic text-bt-primary" data-testid="certificate-name">{name}</p>
            <p className="mx-auto mt-8 max-w-2xl text-xl leading-relaxed">{c.certificate_text}</p>
            <p className="mt-10 font-heading text-2xl text-bt-heading">{c.title}</p>
            <p className="mt-1 text-base text-bt-sagedeep">{date}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const formatDate = (iso) => new Date(iso || Date.now()).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
